import { useEffect, useMemo, useState } from 'react';
import { parseDelimited } from '@/utils/csv-helper';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Phone, RotateCcw, XCircle, AlertTriangle, Filter, Search, Upload, Download, FileSpreadsheet, History, CalendarDays } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { CopyText } from '@/components/ui/CopyText';
import { TrackingDrawer } from '@/features/orders/TrackingDrawer';

/**
 * The number to SHOW for a shipment: LR on B2B, AWB on B2C.
 *
 * A B2B consignment carries both — an LR from our own series and the courier's master AWB — and the
 * LR is what the courier, the customer and the invoice all quote. Reading `awbNo || lrNo` printed
 * the master AWB on every B2B row, so the NDR queue showed a number nobody chasing the delivery had.
 * Same precedence as the `awbOrLr` export column and the label/tracking code.
 */
function refNo(order: { awbNo?: string; lrNo?: string; orderType?: 'B2C' | 'B2B' } | null | undefined): string {
  if (!order) return '';
  return (order.orderType === 'B2B' ? (order.lrNo || order.awbNo) : (order.awbNo || order.lrNo)) || '';
}

interface NdrRow {
  id: number; orderId: string; courierId?: number; ndrCode: number;
  courierRemarks?: string; attemptDate?: string;
  merchantAction?: 'REATTEMPT' | 'CHANGE_ADDRESS' | 'CHANGE_PHONE' | 'RTO';
  merchantRemarks?: string; reattemptDate?: string;
  ivrCalled?: boolean; ivrRemarks?: string;
  actionTakenAt?: string; actionTakenById?: number; actionTakenByName?: string;
  isResolved: boolean; createdAt: string;
  order?: {
    id: string; orderNo: string; awbNo?: string; lrNo?: string; orderType: 'B2C' | 'B2B';
    totalAttempts?: number;
    customerName: string; customerMobile: string;
    customerCity?: string; customerState?: string; customerPincode?: string;
    statusId: number;
    merchant?: { id: number; companyName: string };
    courier?: { id: number; name: string };
  };
}
interface NdrStatus { id: number; name: string }

const TOMORROW = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0,10); })();

/**
 * Calendar date N days back in IST as YYYY-MM-DD, computed from the epoch rather than the browser's
 * own timezone — the backend bounds the day at +05:30, so both ends must agree on which day
 * "today" is even when the operator's machine is set to another zone.
 */
function istDay(daysAgo: number) {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}
/** Date shortcuts: '' = every date, otherwise the number of days back (single day, not a range). */
const DAY_PRESETS = [
  { id: '0', label: 'Today' },
  { id: '1', label: 'Yesterday' },
  { id: '2', label: '2 Days Ago' },
  { id: '', label: 'All Dates' },
] as const;

export function NdrPage() {
  const qc = useQueryClient();
  const [orderType, setOrderType] = useState<'B2C' | 'B2B'>('B2C');
  const [resolved, setResolved] = useState<'0' | '1' | ''>('0');
  const [day, setDay] = useState<'0' | '1' | '2' | ''>('');
  const [search, setSearch] = useState('');
  // The list was hard-capped at 200 rows with no way to reach the rest, so a filter matching more
  // than that silently showed a slice — and the Export (which pages through the FULL match on the
  // server) returned a different, larger set than the screen it was taken from. That mismatch is
  // what "export doesn't match my filters" actually was.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [active, setActive] = useState<NdrRow | null>(null);
  const [mode, setMode] = useState<'call' | 'reattempt' | 'rto' | 'bulk' | 'history' | null>(null);
  // Order whose status history is shown in the right-hand drawer (opened from the NDR reason).
  const [trackOrderId, setTrackOrderId] = useState<string | null>(null);
  const navigate = useNavigate();

  /** Open the full order detail for an NDR row. B2C and B2B have separate detail routes. */
  const openOrder = (r: NdrRow) => {
    if (!r.orderId) return;
    navigate(`/orders-${(r.order?.orderType ?? 'B2C').toLowerCase()}/${r.orderId}`);
  };

  // The chosen shortcut resolved to one IST calendar day; sent as from=to=<day> so the server
  // filters on the courier's attempt date (falling back to when the NDR was raised).
  const dayDate = day === '' ? '' : istDay(Number(day));

  // Debounced so typing an AWB does not fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const searching = debouncedSearch.length > 0;

  // Any filter change puts you back on page 1 — staying on page 7 of a result set that now has two
  // pages shows an empty table and reads as "the filter broke".
  useEffect(() => { setPage(1); }, [resolved, orderType, dayDate, debouncedSearch, pageSize]);

  const { data, isLoading } = useQuery({
    queryKey: ['ndr', { resolved, orderType, dayDate, debouncedSearch, page, pageSize }],
    queryFn: async () => {
      const params: any = { orderType, page, pageSize };
      if (resolved) params.resolved = resolved;
      // The search runs on the SERVER now, across ALL dates. It used to filter only the rows already
      // on screen, so an AWB outside the selected day — or simply on a later page — read as "not
      // found". Whoever pastes an AWB in here has no idea how old the shipment is, which is the
      // whole reason they are searching, so the day filter is deliberately not sent with it.
      if (searching) params.q = debouncedSearch;
      else if (dayDate) { params.from = dayDate; params.to = dayDate; }
      return (await api.get<{ total: number; items: NdrRow[] }>('/ndr', { params })).data;
    },
  });

  const { data: ndrStatuses } = useQuery({
    queryKey: ['ndr-statuses'],
    queryFn: async () => {
      try { const r: any = (await api.get('/settings/ndr_statuses')).data; return (Array.isArray(r) ? r : (r?.items ?? [])) as NdrStatus[]; }
      catch { return [] as NdrStatus[]; }
    },
  });
  const reasonMap = useMemo(() => Object.fromEntries((ndrStatuses ?? []).map(s => [s.id, s.name])), [ndrStatuses]);

  // No client-side filtering left — the server already returned exactly the matching rows.
  const items = data?.items ?? [];
  const total = data?.total ?? items.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  function exportCsv() {
    const qs = new URLSearchParams();
    qs.set('orderType', orderType);
    if (resolved) qs.set('resolved', resolved);
    // Mirrors the list exactly: a search exports every match across all dates, so the file can never
    // hold a different set of rows than the screen it was taken from.
    if (searching) qs.set('q', debouncedSearch);
    else if (dayDate) { qs.set('from', dayDate); qs.set('to', dayDate); }
    downloadBlob(`/ndr/export?${qs.toString()}`, `ndr-${orderType}-${dayDate || new Date().toISOString().slice(0, 10)}.csv`);
  }

  function openModal(row: NdrRow, m: 'call' | 'reattempt' | 'rto' | 'history') {
    setActive(row); setMode(m);
  }
  function closeModal() { setActive(null); setMode(null); }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><AlertTriangle size={22} className="text-amber-500" /> NDR Management</h1>
          <p className="text-sm text-slate-500">Non-Delivery Reports — talk to customer, then re-attempt or RTO</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setMode('bulk')} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
            <Upload size={14} /> Bulk Action
          </button>
        </div>
      </div>

      {/* B2C / B2B tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['B2C', 'B2B'] as const).map(t => (
          <button key={t} onClick={() => setOrderType(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${orderType === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg p-1">
          {[{ id: '0', label: 'Pending' }, { id: '1', label: 'Resolved' }, { id: '', label: 'All' }].map(t => (
            <button key={t.id} onClick={() => setResolved(t.id as any)}
              className={`px-3 py-1 text-sm rounded ${resolved === t.id ? 'bg-white shadow text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Date shortcuts — each picks ONE IST day (not a rolling range), applied to whichever of
            B2C / B2B is on screen so both tabs filter the same way. */}
        <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg p-1">
          <CalendarDays size={13} className="text-slate-400 ml-1" />
          {DAY_PRESETS.map(t => (
            <button key={t.id || 'all'} onClick={() => setDay(t.id as any)}
              title={t.id === '' ? 'No date filter' : `NDRs from ${istDay(Number(t.id))}`}
              className={`px-3 py-1 text-sm rounded ${day === t.id ? 'bg-white shadow text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-[220px] max-w-md relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="AWB / LR / Order / Customer name or phone…"
            className="input h-9 pl-9 w-full text-sm" />
        </div>
        <div className="ml-auto text-xs text-slate-500">
          <Filter size={12} className="inline mr-1" /> {data?.total ?? items.length} {orderType} NDRs
          {/* Only show the day when it was actually applied — during a search it is not. */}
          {searching
            ? <span className="text-amber-600"> · searching all dates</span>
            : dayDate && <span className="text-slate-400"> · {new Date(`${dayDate}T00:00:00`).toLocaleDateString('en-IN')}</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Order</th>
                <th className="text-left px-3 py-2.5">Customer</th>
                <th className="text-left px-3 py-2.5">NDR Reason</th>
                <th className="text-left px-3 py-2.5">Attempt</th>
                <th className="text-left px-3 py-2.5">IVR / Call</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Action By</th>
                <th className="text-left px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={8} className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></td></tr>)}
              {!isLoading && items.length === 0 && (<tr><td colSpan={8} className="text-center py-12 text-slate-400">No {orderType} NDRs{dayDate ? ` on ${new Date(`${dayDate}T00:00:00`).toLocaleDateString('en-IN')}` : ''}.</td></tr>)}
              {items.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-xs">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {/* The AWB/LR opens the full order detail; the copy icon still copies it. */}
                      <CopyText
                        value={refNo(r.order)}
                        display={
                          r.orderId ? (
                            <button
                              type="button"
                              onClick={() => openOrder(r)}
                              title="Open order details"
                              className="font-mono text-primary cursor-pointer underline decoration-primary/50 underline-offset-2 hover:decoration-primary">
                              {refNo(r.order) || '—'}
                            </button>
                          ) : (
                            <span className="font-mono">{refNo(r.order) || '—'}</span>
                          )
                        }
                        iconSize={10} silent />
                      {(r.order?.totalAttempts ?? 0) > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">Attempt {r.order?.totalAttempts}</span>
                      )}
                    </div>
                    <div className="text-slate-500">{r.order?.orderNo} · {r.order?.orderType}</div>
                    <div className="text-slate-400">{r.order?.courier?.name ?? '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="text-slate-900">{r.order?.customerName ?? '—'}</div>
                    <div className="text-slate-500 font-mono">{r.order?.customerMobile ?? '—'}</div>
                    <div className="text-slate-400">{r.order?.customerCity}, {r.order?.customerState}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(() => {
                      const mapped = reasonMap[r.ndrCode];
                      // When the courier's reason isn't mapped to an internal code, show its raw message
                      // (e.g. "Consignee Unavailable") as the primary reason instead of "#0".
                      const primary = mapped || r.courierRemarks || `#${r.ndrCode}`;
                      return (
                        <>
                          {/* The reason opens the order's status history in the right-hand drawer —
                              the scans are what explain WHY the delivery failed. Underlined by
                              DEFAULT (not just on hover) so it reads as a link without having to
                              discover it by mousing over; a history icon reinforces where it goes. */}
                          <button
                            type="button"
                            onClick={() => setTrackOrderId(r.orderId)}
                            title="View order status history"
                            className="font-medium text-rose-700 max-w-[200px] text-left cursor-pointer flex items-center gap-1 underline decoration-rose-400 underline-offset-2 hover:text-rose-800 hover:decoration-rose-700">
                            <span className="truncate">{primary}</span>
                            <History size={11} className="shrink-0 opacity-70" />
                          </button>
                          {mapped && r.courierRemarks && <div className="text-slate-500 truncate max-w-[180px]" title={r.courierRemarks}>{r.courierRemarks}</div>}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.attemptDate ? new Date(r.attemptDate).toLocaleString('en-IN') : '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.ivrCalled ? <span className="text-emerald-700">✓ Called</span> : <span className="text-slate-400">Not called</span>}
                    {r.ivrRemarks && <div className="text-slate-500 max-w-[160px] truncate" title={r.ivrRemarks}>{r.ivrRemarks}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.isResolved
                      ? <span className={`px-2 py-0.5 rounded ${r.merchantAction === 'RTO' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.merchantAction === 'RTO' ? 'RTO' : 'Re-attempt'} {r.reattemptDate ? `@ ${new Date(r.reattemptDate).toLocaleDateString('en-IN')}` : ''}</span>
                      : <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pending</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.actionTakenByName ? (
                      <button onClick={() => openModal(r, 'history')} className="text-slate-700 hover:underline flex items-center gap-1">
                        <History size={11} /> {r.actionTakenByName}
                      </button>
                    ) : <span className="text-slate-400">—</span>}
                    {r.actionTakenAt && <div className="text-slate-500">{new Date(r.actionTakenAt).toLocaleString('en-IN')}</div>}
                  </td>
                  <td className="px-3 py-2">
                    {!r.isResolved && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => openModal(r, 'call')} title="Log call" className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"><Phone size={11} /></button>
                        <button onClick={() => openModal(r, 'reattempt')} title="Re-attempt" className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 flex items-center gap-1"><RotateCcw size={11} /> Re-attempt</button>
                        <button onClick={() => openModal(r, 'rto')} title="Mark RTO" className="text-xs px-2 py-1 bg-rose-50 text-rose-700 border border-rose-200 rounded hover:bg-rose-100 flex items-center gap-1"><XCircle size={11} /> RTO</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Rows-per-page + page controls, matching Remittance/Orders. With these in place the screen
            and the Export finally describe the same set: the filters are identical, and the count
            below is the server's total for those filters, not the length of the current page. */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-slate-100 text-xs text-slate-600">
          <span>
            {total === 0 ? 'No rows' : `Showing ${((page - 1) * pageSize + 1).toLocaleString()}–${Math.min(page * pageSize, total).toLocaleString()} of ${total.toLocaleString()}`}
          </span>
          <span className="flex items-center gap-2">
            Rows per page:
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded px-1.5 py-1">
              {/* Capped at 200: NdrService.list() clamps pageSize to 200, so offering 500 would
                  silently return 200 and the footer count would contradict the rows on screen. */}
              {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-slate-200 disabled:opacity-30 hover:bg-slate-50" title="Previous page">‹</button>
            <span className="tabular-nums">Page {page} of {lastPage}</span>
            <button disabled={page >= lastPage} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 rounded border border-slate-200 disabled:opacity-30 hover:bg-slate-50" title="Next page">›</button>
          </span>
        </div>
      </div>

      {active && (mode === 'call' || mode === 'reattempt' || mode === 'rto') && (
        <Modal isOpen={true} onClose={closeModal}
          title={mode === 'call' ? 'Log Customer Call' : mode === 'reattempt' ? 'Schedule Re-attempt' : 'Mark RTO'}
          subtitle={`${refNo(active.order)} · ${active.order?.customerName ?? ''}`} maxWidth="md">
          <NdrActionForm ndr={active} mode={mode} onClose={closeModal} onSuccess={() => { closeModal(); qc.invalidateQueries({ queryKey: ['ndr'] }); }} />
        </Modal>
      )}
      {active && mode === 'history' && (
        <Modal isOpen={true} onClose={closeModal} title="Action History"
          subtitle={`${refNo(active.order)} · NDR #${active.id}`} maxWidth="md">
          <div className="space-y-3 text-sm">
            <Row k="NDR raised at" v={new Date(active.createdAt).toLocaleString('en-IN')} />
            <Row k="Attempt at" v={active.attemptDate ? new Date(active.attemptDate).toLocaleString('en-IN') : '—'} />
            <Row k="Courier remarks" v={active.courierRemarks ?? '—'} />
            <Row k="IVR / Call" v={active.ivrCalled ? `Called${active.ivrRemarks ? ` — ${active.ivrRemarks}` : ''}` : 'Not called'} />
            {active.isResolved && <>
              <Row k="Action taken" v={active.merchantAction ?? '—'} />
              <Row k="Action by" v={`${active.actionTakenByName ?? `User #${active.actionTakenById}`}`} />
              <Row k="Action at" v={active.actionTakenAt ? new Date(active.actionTakenAt).toLocaleString('en-IN') : '—'} />
              {active.merchantAction === 'REATTEMPT' && <Row k="Re-attempt date" v={active.reattemptDate ? new Date(active.reattemptDate).toLocaleDateString('en-IN') : '—'} />}
              <Row k="Action remarks" v={active.merchantRemarks ?? '—'} />
            </>}
          </div>
        </Modal>
      )}
      {mode === 'bulk' && (
        <Modal isOpen={true} onClose={closeModal} title="Bulk NDR Action" subtitle={`Apply re-attempt or RTO to many ${orderType} AWBs via CSV`} maxWidth="2xl">
          <BulkActionDialog orderType={orderType} onClose={closeModal} onDone={() => { closeModal(); qc.invalidateQueries({ queryKey: ['ndr'] }); }} />
        </Modal>
      )}

      {/* Order status history — the same right-hand drawer the Orders list uses, so a scan timeline
          looks and behaves identically wherever it's opened from. */}
      <TrackingDrawer orderId={trackOrderId} onClose={() => setTrackOrderId(null)} />
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="text-xs text-slate-500 w-32 shrink-0">{k}</div>
      <div className="text-slate-900">{v}</div>
    </div>
  );
}

function NdrActionForm({ ndr, mode, onClose, onSuccess }: { ndr: NdrRow; mode: 'call' | 'reattempt' | 'rto'; onClose: () => void; onSuccess: () => void }) {
  const [remarks, setRemarks] = useState('');
  const [date, setDate] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const mut = useMutation({
    mutationFn: async () => {
      if (mode === 'call') return (await api.post(`/ndr/${ndr.id}/call`, { ivrCalled: true, remarks })).data;
      if (mode === 'reattempt') {
        if (!date) throw new Error('Pick a re-attempt date');
        if (date <= new Date().toISOString().slice(0,10)) throw new Error('Re-attempt date must be tomorrow or later');
        return (await api.post(`/ndr/${ndr.id}/reattempt`, { date, remarks, newAddress: newAddress || undefined, newPhone: newPhone || undefined })).data;
      }
      return (await api.post(`/ndr/${ndr.id}/rto`, { remarks })).data;
    },
    onSuccess: () => { toast.success(mode === 'call' ? 'Call logged' : mode === 'reattempt' ? 'Re-attempt scheduled' : 'RTO marked'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Action failed'),
  });

  return (
    <div className="space-y-4">
      {mode === 'reattempt' && (
        <>
          <div>
            <label className="text-xs font-medium uppercase text-slate-500 mb-1 block">Next attempt date * <span className="text-slate-400 font-normal normal-case">(must be tomorrow or later)</span></label>
            <input type="date" value={date} min={TOMORROW} onChange={(e) => setDate(e.target.value)} className="input w-full" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium uppercase text-slate-500 mb-1 block">New address (optional)</label><input value={newAddress} onChange={(e) => setNewAddress(e.target.value)} className="input w-full" /></div>
            <div><label className="text-xs font-medium uppercase text-slate-500 mb-1 block">New phone (optional)</label><input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} className="input w-full font-mono" maxLength={10} /></div>
          </div>
        </>
      )}
      <div>
        <label className="text-xs font-medium uppercase text-slate-500 mb-1 block">Remarks</label>
        <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} className="input w-full" placeholder={mode === 'call' ? 'What did the customer say?' : mode === 'reattempt' ? 'Note for delivery boy' : 'Reason for RTO'} />
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
        <button onClick={() => mut.mutate()} disabled={mut.isPending}
          className={`px-5 py-2 text-sm rounded text-white flex items-center gap-2 ${mode === 'rto' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-primary hover:bg-primary/90'} disabled:opacity-50`}>
          {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
          {mode === 'call' ? 'Save Call' : mode === 'reattempt' ? 'Schedule Re-attempt' : 'Confirm RTO'}
        </button>
      </div>
    </div>
  );
}

function BulkActionDialog({ orderType, onClose, onDone }: { orderType: 'B2C' | 'B2B'; onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<any>(null);

  const idLabel = orderType === 'B2C' ? 'AWB' : 'LR';
  const sample = `${idLabel},action,date,newAddress,newPhone,remarks
GNT0010000001,reattempt,${TOMORROW},,,Customer asked for tomorrow
GNT0010000003,rto,,,,Customer not interested`;

  const upload = useMutation({
    mutationFn: async () => {
      const rows = parseCsv(text);
      return (await api.post('/ndr/bulk-action', { rows })).data;
    },
    onSuccess: (data: any) => {
      setResult(data);
      if (data.ok > 0) toast.success(`Processed ${data.ok}, ${data.failed} failed`);
      if (data.failed === 0) setTimeout(() => onDone(), 1500);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Bulk action failed'),
  });

  function downloadSample() {
    const a = document.createElement('a');
    const blob = new Blob([sample], { type: 'text/csv' });
    a.href = URL.createObjectURL(blob);
    a.download = `ndr-bulk-action-${orderType.toLowerCase()}.csv`;
    a.click();
  }

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
        <div className="flex items-start gap-2">
          <FileSpreadsheet size={16} className="text-emerald-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-900 mb-0.5">CSV columns</div>
            <p className="text-xs text-emerald-800">
              Required: <strong>{idLabel}</strong>, <strong>action</strong> (one of <code>reattempt</code> or <code>rto</code>). For re-attempt, also supply <strong>date</strong> (YYYY-MM-DD, tomorrow or later). Optional: <em>newAddress, newPhone, remarks</em>.
            </p>
            <button onClick={downloadSample} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">
              <Download size={12} /> Download Sample CSV
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium uppercase text-slate-500 mb-1 block">Paste CSV content</label>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
          placeholder={sample} className="input font-mono text-xs w-full" />
      </div>

      {result && (
        <div className="rounded-lg border">
          <div className="grid grid-cols-3 divide-x divide-slate-200 text-center bg-white">
            <div className="p-3"><div className="text-xs text-slate-500">Rows</div><div className="text-xl font-bold">{(result.ok ?? 0) + (result.failed ?? 0)}</div></div>
            <div className="p-3 bg-emerald-50"><div className="text-xs text-emerald-700">Done</div><div className="text-xl font-bold text-emerald-700">{result.ok ?? 0}</div></div>
            <div className="p-3 bg-rose-50"><div className="text-xs text-rose-700">Failed</div><div className="text-xl font-bold text-rose-700">{result.failed ?? 0}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="p-3 bg-amber-50 text-xs">
              <div className="font-medium mb-1">Errors:</div>
              <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((e: any, i: number) => <li key={i}><b>{e.awb}:</b> {e.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Close</button>
        <button onClick={() => upload.mutate()} disabled={!text.trim() || upload.isPending}
          className="px-5 py-2 bg-primary text-white text-sm rounded font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {upload.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Run Bulk Action
        </button>
      </div>
    </div>
  );
}

/**
 * NDR upload rows.
 *
 * This was a bare `line.split(',')` with no quote handling whatsoever, so a comma anywhere in any
 * field — an address, a remark — shifted every later column and corrupted the row silently. Now on
 * the shared quote-aware tokeniser (see utils/csv-helper), which also copes with quoted newlines,
 * Excel's BOM and tab/semicolon separators.
 */
function parseCsv(text: string): Array<Record<string, string>> {
  const norm = (h: string) => {
    const k = h.toLowerCase().trim();
    if (k === 'lr' || k === 'lrno' || k === 'lr no') return 'awb';
    if (k === 'awbno' || k === 'awb no' || k === 'awb number') return 'awb';
    return k;
  };
  return parseDelimited(text, { mapHeader: norm }).rows;
}
